/**
 * @fileoverview The season brief: everything the Seasons tab and the season
 * review decide, worked out by the backend (services/season/seasonBrief)
 * and sent as codes with finished sentences in the gardener's language, so
 * the pages only lay it out. The season the header names and its one
 * sentence; Recent and coming up; what counts as the season; the season offered on
 * Your Seasons and the milestone ask due; the review page's season, words,
 * counts, firsts, picture words and starting photos; the beds; and the
 * diary by day, with the garden's real frost dates among its rows (D58); and
 * the diary's months with their names and the month Seasons opens on, from
 * the garden's own day (W24 Job H: months, openMonth, openMonthByBed).
 *
 * Every said thing carries a stable code, its values (dates as
 * "YYYY-MM-DD") and its text; the app lays out by kind and shows the text.
 *
 * Where: features/season (SeasonPage, SeasonReviewPage, SeasonHeader,
 * SeasonReviewEntry, SeasonMoments, through lib/useSeason) and
 * shared/api/locations (a bed's Lately is the diary's own rows, sent on the
 * bed list since W24 Job H).
 * API: GET /season?zip=&year=&choice=&lang=&unit= (for the garden's own day, the server's). With no choice
 * the server uses the one kept on the gardener's account (preferences.seasonReviewBy; audit A72).
 *
 * @module api/season
 */

import { apiClient } from './apiClient';
import { gardenZip } from './gardenMembers';
import type { VerdictToken } from '@/shared/theme/verdictTokens';

/** One thing the brief says: its code, the values it was said from, and the finished text. */
export interface SeasonSaid {
  /** The dictionary code: "season.header.sown", "seasonReview.held.firstHarvest", "countdown.first". */
  code: string;
  /** The raw values: dates as "YYYY-MM-DD", counts, words. */
  values: Record<string, unknown>;
  /** The sentence in the requested language. */
  text: string;
}

/** What a season means for the review: frost to frost, or January to December. */
export type SeasonBy = 'growing' | 'calendar';

/** A season's days for the review, and what it was asked to be. */
export interface SeasonReviewSpan {
  /** The year the season is named for. */
  year: number;
  /** What the season is: the choice, or calendar where there were no frost dates. */
  by: SeasonBy;
  /** The growing season was asked for and there were no frost dates, so this is the calendar year. */
  fellBack: boolean;
  /** Its first day, "YYYY-MM-DD". */
  from: string;
  /** Its last day, "YYYY-MM-DD". */
  to: string;
}

/** The season the header names, where the gardener is. */
export interface SeasonNow {
  name: 'spring' | 'summer' | 'fall' | 'winter';
  /** The year said with the name: the current one. */
  year: number;
  /** First day, "YYYY-MM-DD". */
  from: string;
  /** Last day, "YYYY-MM-DD". */
  to: string;
  /** "Fall 2026". */
  title: SeasonSaid;
}

/** A milestone reached this season: a clause in the header's sentence, a link to its month. */
export interface SeasonMilestone extends SeasonSaid {
  key: 'seed' | 'transplant' | 'harvest';
  /** The first day it happened, "YYYY-MM-DD". */
  day: string;
  /** Its month, "YYYY-MM", for the Months filter. */
  month: string;
  /** The words before the clause: ", " or " and ". */
  joiner: string;
}

/** The season's name and one sentence of what it has done. */
export interface SeasonHeaderBrief {
  title: SeasonSaid;
  /** The plants growing now, and the beds among them. */
  plants: number;
  beds: number;
  /** "5 plants in 2 beds". */
  counts: SeasonSaid;
  milestones: SeasonMilestone[];
  /** "Nothing new this season yet.", or null. */
  quiet: SeasonSaid | null;
  /** The whole sentence. */
  text: string;
}

/** One line of Recent and coming up. */
export interface SeasonAheadLine extends SeasonSaid {
  /** Unique in the list. */
  key: string;
  kind: 'frost' | 'freeze' | 'planting' | 'moon' | 'turn';
  /** The day it is sorted by, "YYYY-MM-DD". */
  date: string;
  /** The day shown beside it ("Oct 19"). */
  dateText: string;
  /** The small line under it (a range's typical date and what it does), or null. */
  sub: string | null;
  /** Where the line leads, or null. */
  link: string | null;
  /** A planting line's plants. */
  plantIds: number[];
  /** A planting window open now: until when, or null. */
  until: SeasonSaid | null;
  /** A range: fall's firsts or spring's lasts. */
  season?: 'first' | 'last';
  /** A range: not begun yet, begun and not yet over, or (fall's first frost, B144) over with no frost yet. */
  state?: 'ahead' | 'underway' | 'passed';
  /** A moon. */
  phase?: 'new' | 'full';
  /** A range: the colour its line is drawn in (the server's condition_colour table); none for the ink. */
  token?: VerdictToken | null;
}

/** What counts as the season, as the review page offers it. */
export interface SeasonChoiceBrief {
  /** The choice used: the one sent, else the one kept on the account, else growing. */
  by: SeasonBy;
  /** The growing season was asked for and there were no frost dates. */
  fellBack: boolean;
  /** "What counts as the season". */
  label: SeasonSaid;
  /** Both choices, in the order they are offered, each with its words. */
  values: Array<SeasonSaid & { value: SeasonBy }>;
}

/** What a season held: its counts and firsts. */
export interface SeasonCounts {
  plants: number;
  beds: number;
  photos: number;
}

/** A season's firsts, each "YYYY-MM-DD" or null (never "none"). */
export interface SeasonFirsts {
  planting: string | null;
  harvest: string | null;
}

/** The season Your Seasons offers a review of. */
export interface SeasonOffered {
  year: number;
  span: SeasonReviewSpan;
  /** True once it has ended ("Your 2026 season"); false while it is "so far". */
  ended: boolean;
  counts: SeasonCounts;
  firsts: SeasonFirsts;
  /** The entry's words: "Your season so far", and its hint. */
  entry: { title: SeasonSaid; hint: SeasonSaid };
}

/** The milestone ask due now. */
export interface SeasonDue {
  moment: 'harvest' | 'midsummer' | 'end';
  /** The tip key it is marked under (season_harvest...). */
  key: string;
  /** The season's year, the mark's scope. */
  year: number;
  title: SeasonSaid;
  body: SeasonSaid;
  /** "Share" or "See it". */
  button: SeasonSaid;
  /** "Asked once this season…". */
  once: SeasonSaid;
}

/** One of the picture's numbers: "12" over "plants". */
export interface SeasonStat {
  value: string;
  label: string;
}

/** One of the gardener's photos the season's picture may carry. */
export interface SeasonCandidate {
  photoId: number;
  plantObjId: number;
  plantName: string;
  /** The day it was taken, "YYYY-MM-DD". */
  day: string;
}

/** The review page for one season. */
export interface SeasonReviewBrief {
  year: number;
  /** The season under way, the latest year the address answers for. */
  currentYear: number;
  span: SeasonReviewSpan;
  ended: boolean;
  enoughToShow: boolean;
  /** "Your 2026 season" or "Your season so far". */
  title: SeasonSaid;
  /** What the season's days are, in words. */
  days: SeasonSaid;
  /** "So far: the season ends November 1.", or null once ended. */
  soFar: SeasonSaid | null;
  counts: SeasonCounts;
  firsts: SeasonFirsts;
  /** Nothing at all was recorded in it. */
  nothing: boolean;
  nothingText: SeasonSaid;
  /** What it held, a row each. */
  held: Array<SeasonSaid & { key: 'plants' | 'planting' | 'harvest' | 'photos' }>;
  /** The picture's words. */
  frame: { title: SeasonSaid; spanLine: SeasonSaid; stats: SeasonStat[]; lines: SeasonSaid[]; caption: SeasonSaid };
  /** The plants' photos taken in it, oldest first, none under review. */
  candidates: SeasonCandidate[];
  /** The photos the picture starts with, by id, oldest first. */
  startPhotos: number[];
  entry: { title: SeasonSaid; hint: SeasonSaid };
}

/** A diary row's photo. */
export interface SeasonDiaryPhoto {
  photoId: number;
  /** A signed link to the picture, or null while under review. */
  url: string | null;
  /** A signed link to its thumbnail, or null while under review. */
  thumbUrl: string | null;
  /** Grow's safety check flagged it; the API client fills its links with the "being reviewed" picture. */
  under_review?: boolean;
}

/**
 * What happened: a plant added, a stage reached, a photo, a note, a harvest, a plant taken out; or the weather's real
 * frost dates (D58: the season's first frost and first freeze, spring's last frost and last freeze, each on its night,
 * about no plant or bed).
 */
export type SeasonDiaryKind =
  | 'added' | 'stage' | 'photo' | 'note' | 'harvest' | 'removed'
  | 'frostFirst' | 'freezeFirst' | 'frostLast' | 'freezeLast';

/** One row of the diary. */
export interface SeasonDiaryRow extends SeasonSaid {
  /** Unique across plants and beds. */
  key: string;
  /** "YYYY-MM-DD". */
  day: string;
  kind: SeasonDiaryKind;
  /** The plant, or null for a bed's own photo or note. */
  plantObjId: number | null;
  /** The plant's name, the bed's for its own entries, or '' for a frost row. */
  plantName: string;
  /** The bed it happened in, or null for a plant with none or a frost row. */
  bedId: number | null;
  bedName: string;
  /** The words: a note, a photo's caption, a harvest; or null. */
  note: string | null;
  /** A frost row's night low, said in the reader's unit (code season.frost.low, D58); absent on every other row. */
  low?: SeasonSaid;
  stageId: number | null;
  /** A stage event's name in the reader's language, or null. */
  stageName: string | null;
  /** "estimated", for a stage Grow estimated; null for one recorded. */
  estimated: SeasonSaid | null;
  /** An Added row for a plant added from an older photo (B81): "Photo taken Oct 20" (code season.photoTaken); null on every other row. */
  photoTaken?: SeasonSaid | null;
  /** A photo row's photo, or, on that Added row, the older photo the plant was added from; else null. */
  photo: SeasonDiaryPhoto | null;
}

/** Plants added to one bed on one day, several at once: one row that opens. */
export interface SeasonDiaryGroup extends SeasonSaid {
  key: string;
  day: string;
  kind: 'addedGroup';
  count: number;
  bedId: number | null;
  bedName: string;
  rows: SeasonDiaryRow[];
}

/** One day of the diary. */
export interface SeasonDiaryDay {
  /** "YYYY-MM-DD". */
  day: string;
  /** "Today", "Yesterday", or the date. */
  title: SeasonSaid;
  rows: Array<SeasonDiaryRow | SeasonDiaryGroup>;
}

/** A bed any plant has been in, for the Beds filter. */
export interface SeasonBed {
  id: number;
  name: string;
  /** Still on Beds, where a bed's own entry can lead. */
  live: boolean;
}

/** One month of the diary, for the month filter: its key and its name in the brief's language. */
export interface SeasonMonth {
  /** "YYYY-MM". */
  key: string;
  /** "October 2026". */
  text: string;
}

/** The brief. */
export interface SeasonBrief {
  success: boolean;
  /** The gardener's day, "YYYY-MM-DD". */
  date: string;
  lang: string;
  unit: 'F' | 'C';
  /** The garden's IANA zone, which dates its recorded moments. */
  timeZone: string;
  season: SeasonNow;
  header: SeasonHeaderBrief;
  ahead: SeasonAheadLine[];
  choice: SeasonChoiceBrief;
  /** The season Your Seasons offers, or null. */
  offered: SeasonOffered | null;
  /** The milestone ask due now, or null. */
  due: SeasonDue | null;
  review: SeasonReviewBrief;
  beds: SeasonBed[];
  /** The beds still on Beds, by id. */
  liveBeds: number[];
  /** Every day, newest first. */
  diary: SeasonDiaryDay[];
  /** Every month from the diary's first day to the garden's month, newest first, empty ones too (the month filter's options). */
  months: SeasonMonth[];
  /** The month Seasons opens on ("YYYY-MM"): the garden's month if the diary has anything in it, else the latest month that has. */
  openMonth: string;
  /** The same rule for each bed alone, by bed id: with beds chosen, Seasons opens on the newest of theirs. */
  openMonthByBed: Record<string, string>;
}

/** What the brief is asked for. */
export interface SeasonRequest {
  /** The garden's postcode (useGardenPostcode); sent only in the person's own garden (gardenZip). No day is sent: the
   *  brief is for the garden's own day, the server's (architecture audit A31). */
  zip: string;
  /** The words' language. */
  lang: string;
  /** The frost temperatures' unit. */
  unit: 'F' | 'C';
  /** The season the review page asks for; left out, the season offered. */
  year?: number | null;
  /** What the gardener chose a season to be; left out, the server reads the one kept on their account. */
  choice?: SeasonBy;
}

/**
 * The season brief for the signed-in gardener and the garden they tend.
 *
 * @param request - What to ask for.
 * @returns The brief.
 */
export async function getSeason({ zip, lang, unit, year = null, choice }: SeasonRequest): Promise<SeasonBrief> {
  const response = await apiClient.get<SeasonBrief>('/season', {
    params: { zip: gardenZip(zip), lang, unit, ...(choice ? { choice } : {}), ...(year != null ? { year } : {}) },
  });
  return response.data;
}

